import { useEffect, useRef, useState } from 'react';
import { X } from 'lucide-react';
import { Button, IconButton } from './ui.jsx';

/** Corpo rolável do diálogo (use com `raw` quando o conteúdo tiver estado próprio). */
export function ModalBody({ children }) {
  return (
    <div className="modal-body" tabIndex={-1}>
      {children}
    </div>
  );
}

export function ModalFooter({ children }) {
  return <div className="modal-footer">{children}</div>;
}

/**
 * Diálogo acessível com <dialog> nativo: foco preso, Esc fecha, foco volta ao gatilho.
 * No celular vira folha inferior (CSS) com rodapé fixo, funcionando com teclado virtual.
 * O conteúdo só é montado enquanto aberto: formulários começam com estado limpo a cada
 * abertura. Com `raw`, os filhos renderizam <ModalBody> e <ModalFooter> por conta própria.
 */
export function Modal({
  open,
  onClose,
  title,
  children,
  footer,
  size,
  initialFocus = true,
  labelledBy,
  raw,
}) {
  const ref = useRef(null);
  const lastFocus = useRef(null);

  useEffect(() => {
    const d = ref.current;
    if (!d) return;
    if (open && !d.open) {
      lastFocus.current = document.activeElement;
      d.showModal();
      if (initialFocus) {
        const first = d.querySelector('[autofocus], input:not([type=hidden]), select, textarea');
        (first ?? d.querySelector('.modal-body'))?.focus?.();
      }
    } else if (!open && d.open) {
      d.close();
      lastFocus.current?.focus?.();
    }
  }, [open, initialFocus]);

  useEffect(() => () => ref.current?.open && ref.current.close(), []);

  return (
    <dialog
      ref={ref}
      className={`modal${size === 'lg' ? ' modal-lg' : ''}`}
      aria-labelledby={labelledBy}
      aria-label={labelledBy ? undefined : title}
      onCancel={(e) => {
        e.preventDefault();
        onClose?.();
      }}
      onMouseDown={(e) => {
        if (e.target === ref.current) onClose?.();
      }}
    >
      {open && (
        <>
          <div className="modal-header">
            <h2>{title}</h2>
            <IconButton icon={X} label="Fechar" onClick={onClose} />
          </div>
          {raw ? (
            children
          ) : (
            <>
              <ModalBody>{children}</ModalBody>
              {footer && <ModalFooter>{footer}</ModalFooter>}
            </>
          )}
        </>
      )}
    </dialog>
  );
}

/** Confirmação explícita para ações destrutivas ou incertas. */
export function ConfirmDialog({
  open,
  title,
  children,
  confirmLabel = 'Confirmar',
  danger,
  onConfirm,
  onClose,
}) {
  const [busy, setBusy] = useState(false);
  return (
    <Modal
      open={open}
      onClose={busy ? undefined : onClose}
      title={title}
      initialFocus={false}
      footer={
        <>
          <Button onClick={onClose} disabled={busy}>
            Cancelar
          </Button>
          <Button
            variant={danger ? 'danger' : 'primary'}
            loading={busy}
            onClick={async () => {
              setBusy(true);
              try {
                await onConfirm();
              } finally {
                setBusy(false);
              }
            }}
          >
            {confirmLabel}
          </Button>
        </>
      }
    >
      <div className="stack">{children}</div>
    </Modal>
  );
}
